<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                Academic Years Management
            </h1>
            <button @click="openModal" class="btn btn-primary">
                <Plus class="w-4 h-4 mr-1" />
                Add Academic Year
            </button>
        </div>

        <!-- Search and Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="space-y-3">
                <!-- Search -->
                <input
                    v-model="searchQuery"
                    @input="debouncedSearch"
                    type="text"
                    placeholder="Search by name..."
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />

                <div class="flex flex-wrap gap-3">
                    <!-- Status Filter -->
                    <SearchableSelect
                        v-model="filters.is_active"
                        :options="statusOptions"
                        placeholder="All Status"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchAcademicYears"
                    />

                    <button
                        @click="resetFilters"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <RotateCcw class="w-4 h-4" />
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div
            v-if="academicYearStore.loading"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <SkeletonLoader variant="table" :rows="6" :cols="5" />
        </div>

        <!-- Error State -->
        <div
            v-if="academicYearStore.error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4 mb-6"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-danger-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ academicYearStore.error }}
                    </p>
                    <button
                        @click="academicYearStore.clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <!-- Data Table -->
        <div
            v-if="!academicYearStore.loading && !academicYearStore.error"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                    style="width: 100%"
                >
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Name
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Start Date
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                End Date
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Status
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                    >
                        <tr
                            v-for="academicYear in academicYearStore.academicYears"
                            :key="academicYear.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <td class="px-6 py-4 whitespace-nowrap">
                                <div
                                    class="text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    {{ academicYear.name }}
                                </div>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ formatDate(academicYear.start_date) }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ formatDate(academicYear.end_date) }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    v-if="academicYear.is_active"
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200"
                                >
                                    Active
                                </span>
                                <span
                                    v-else
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                                >
                                    Inactive
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                            >
                                <button
                                    @click="editAcademicYear(academicYear)"
                                    title="Edit Academic Year"
                                    class="inline-flex items-center justify-center p-2 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors mr-2"
                                >
                                    <Pencil class="w-4 h-4" />
                                </button>
                                <button
                                    v-if="!academicYear.is_active"
                                    @click="setActive(academicYear)"
                                    title="Set Active"
                                    class="inline-flex items-center justify-center p-2 text-success-600 dark:text-success-400 hover:text-success-900 dark:hover:text-success-300 rounded-md hover:bg-success-50 dark:hover:bg-success-900/20 transition-colors mr-2"
                                >
                                    <CheckCircle class="w-4 h-4" />
                                </button>
                                <button
                                    @click="confirmDelete(academicYear)"
                                    title="Delete Academic Year"
                                    class="inline-flex items-center justify-center p-2 text-danger-600 dark:text-danger-400 hover:text-danger-900 dark:hover:text-danger-300 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                                >
                                    <Trash2 class="w-4 h-4" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Empty State -->
            <EmptyState
                v-if="academicYearStore.academicYears.length === 0"
                :icon="Calendar"
                title="No academic years found"
                description="Get started by adding a new academic year."
            >
                <button @click="openModal" class="btn btn-primary">
                    <Plus class="w-4 h-4 mr-1" />
                    Add Academic Year
                </button>
            </EmptyState>

            <!-- Pagination -->
            <BasePagination
                :pagination="academicYearStore.meta"
                :per-page="academicYearStore.perPage"
                @page-change="goToPage"
                @per-page-change="handlePerPageChange"
            />
        </div>

        <!-- Academic Year Modal -->
        <AcademicYearModal
            v-if="showModal"
            :academic-year="selectedAcademicYear"
            :is-editing="isEditing"
            @close="closeModal"
            @saved="handleSave"
        />

        <!-- Delete Confirmation Modal -->
        <DeleteConfirmationModal
            :show="showDeleteModal"
            title="Delete Academic Year"
            :message="
                academicYearToDelete
                    ? `Are you sure you want to delete ${academicYearToDelete.name}? This action cannot be undone.`
                    : ''
            "
            @confirm="handleDelete"
            @close="closeDeleteModal"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useAcademicYearStore } from "@/stores/academicYear";
import AcademicYearModal from "@/components/AcademicYearModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import SearchableSelect from "@/components/SearchableSelect.vue";
import BasePagination from "@/components/BasePagination.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import NotificationService from "@/utils/notifications";
import type { AcademicYear } from "@/api/academicYear";
import {
    Plus,
    RotateCcw,
    Pencil,
    CheckCircle,
    Trash2,
    Calendar,
} from "@lucide/vue";

const academicYearStore = useAcademicYearStore();
// State
const searchQuery = ref("");
const filters = ref({
    is_active: null as boolean | null,
});
const showModal = ref(false);
const showDeleteModal = ref(false);
const selectedAcademicYear = ref<AcademicYear | null>(null);
const isEditing = ref(false);
const academicYearToDelete = ref<AcademicYear | null>(null);

// Computed

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.is_active !== null ||
        academicYearStore.page !== 1 ||
        academicYearStore.perPage !== 10
    );
});

// Options for Status filter
const statusOptions = [
    { value: null, label: "All Status" },
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
];

// Methods
const handlePerPageChange = (value: number) => {
    academicYearStore.perPage = value;
    academicYearStore.page = 1;
    fetchAcademicYears();
};

const resetFilters = () => {
    // Reset local component state
    searchQuery.value = "";
    filters.value.is_active = null;

    // Reset store state
    academicYearStore.resetFilters();
};

const fetchAcademicYears = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
        is_active: filters.value.is_active, // Explicitly pass the is_active filter
    };

    await academicYearStore.fetchAcademicYears(searchFilters);
};

const debouncedSearch = useDebounce(fetchAcademicYears, 300);

const openModal = () => {
    selectedAcademicYear.value = null;
    isEditing.value = false;
    showModal.value = true;
    // Clear any previous errors when opening modal
    academicYearStore.clearError();
};

const editAcademicYear = (academicYear: AcademicYear) => {
    selectedAcademicYear.value = academicYear;
    isEditing.value = true;
    showModal.value = true;
    // Clear any previous errors when opening modal
    academicYearStore.clearError();
};

const closeModal = () => {
    showModal.value = false;
    selectedAcademicYear.value = null;
    isEditing.value = false;
};

const confirmDelete = (academicYear: AcademicYear) => {
    academicYearToDelete.value = academicYear;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    academicYearToDelete.value = null;
};

const handleSave = () => {
    closeModal();
    showNotification(
        isEditing.value
            ? "Academic year updated successfully"
            : "Academic year created successfully",
        "success",
    );
    academicYearStore.clearError();
};

const handleDelete = async () => {
    try {
        if (academicYearToDelete.value) {
            await academicYearStore.deleteAcademicYear(
                academicYearToDelete.value.id,
            );
            showNotification("Academic year deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete academic year", "error");
    }
};

const setActive = async (academicYear: AcademicYear) => {
    try {
        await academicYearStore.setActiveAcademicYear(academicYear.id);
        showNotification("Academic year set as active successfully", "success");
    } catch (error) {
        showNotification("Failed to set academic year as active", "error");
    }
};

const goToPage = (page: number) => {
    academicYearStore.setPage(page);
};

const showNotification = (message: string, type: "success" | "error") => {
    if (type === "success") {
        NotificationService.success(message);
    } else {
        NotificationService.error(message);
    }
};

const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    fetchAcademicYears();
});
</script>
